// ═══════════════════════════════════════════════════════════════════════════
// MuhurtaReportTab.js — ₹149 Event Muhurta Report
// Flow: activity selector → name entry → loading → date cards + narrative
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// Matches JyotishPage.js's JT token system — white, one accent, plain Inter.
const RT = {
  page:     "#ffffff",
  card:     "#ffffff",
  cardWarm: "#fafafa",
  border:   "#e2e2e2",
  shadow:   "0 1px 3px rgba(0,0,0,0.08)",
  font:     "'Inter',sans-serif",
  text:     "#1a1a1a",
  textDim:  "#5a5a5a",
  accent:   "#c2560f",
};

// Each activity keeps a distinct, readable category color (used sparingly —
// selection state + card accent only, never as the page's competing brand color).
const ACTIVITIES = {
  marriage:  { label: "Wedding / Marriage",    icon: "💒", color: "#be185d", months: 6 },
  business:  { label: "Business Launch",       icon: "🏢", color: "#a16207", months: 3 },
  property:  { label: "Home / Property",       icon: "🏠", color: "#15803d", months: 4 },
  education: { label: "Study / Education",     icon: "📚", color: "#4338ca", months: 2 },
  interview: { label: "Career / Interview",    icon: "💼", color: "#0f766e", months: 1.5 },
  travel:    { label: "Travel / Journey",      icon: "✈️", color: "#c2410c", months: 1 },
  medical:   { label: "Medical Procedure",     icon: "🏥", color: "#7e22ce", months: 1 },
};

const SECTION_ICONS = {
  "window":      "🗓",
  "best date":   "⭐",
  "second":      "🌟",
  "other":       "✦",
  "what to do":  "🙏",
};

function getSectionIcon(title) {
  const t = title.toLowerCase();
  for (const [key, icon] of Object.entries(SECTION_ICONS)) {
    if (t.includes(key)) return icon;
  }
  return "✦";
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

// ── Shimmer loader ─────────────────────────────────────────────────────────────
function Shimmer({ months }) {
  const s = {
    background: "linear-gradient(90deg,#f0f0f0 25%,#e2e2e2 50%,#f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.6s infinite",
    borderRadius: 8,
  };
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20, padding:"8px 0" }}>
      {[1,2,3].map(i => (
        <div key={i} style={{ borderRadius:12, border:`1px solid ${RT.border}`,
                              padding:"14px", display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ ...s, height:16, width:"60%" }} />
          <div style={{ ...s, height:12, width:"40%" }} />
          <div style={{ display:"flex", gap:8 }}>
            <div style={{ ...s, height:28, width:"45%" }} />
            <div style={{ ...s, height:28, width:"45%" }} />
          </div>
        </div>
      ))}
      <p style={{ textAlign:"center", color:RT.textDim, fontSize:"0.95rem", marginTop:4 }}>
        Scanning {months >= 1 ? months + " months" : "6 weeks"} for auspicious dates... ~20 seconds
      </p>
    </div>
  );
}

// ── Single auspicious date card ────────────────────────────────────────────────
function DateCard({ r, color, idx }) {
  const dt = new Date(r.date + "T00:00:00");
  const dayName = dt.toLocaleDateString("en-IN", { weekday:"long" });
  const dateStr = dt.toLocaleDateString("en-IN", { day:"numeric", month:"long", year:"numeric" });
  const scoreColor = r.score >= 75 ? "#15803d" : r.score >= 60 ? "#a16207" : RT.textDim;

  return (
    <div style={{
      borderRadius:12, border:`1px solid ${color}40`,
      background:RT.card, padding:"14px",
      position:"relative", overflow:"hidden",
    }}>
      {/* Rank badge */}
      <div style={{
        position:"absolute", top:10, right:12,
        background:`rgba(${hexToRgb(color)},0.1)`,
        border:`1px solid ${color}60`,
        borderRadius:16, padding:"2px 10px",
        fontSize:"0.95rem", fontWeight:700, color,
      }}>
        #{idx}
      </div>

      {/* Date */}
      <div style={{ fontFamily:RT.font, fontSize:"1.02rem",
                    color:RT.text, fontWeight:700, marginBottom:2, paddingRight:60 }}>
        {dayName}
      </div>
      <div style={{ color:RT.textDim, fontSize:"0.95rem", marginBottom:10 }}>
        {dateStr}
      </div>

      {/* Score bar */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
        <div style={{ flex:1, height:5, borderRadius:4,
                      background:"#f0f0f0" }}>
          <div style={{ height:"100%", borderRadius:4,
                        width:`${r.score}%`,
                        background:`linear-gradient(90deg,${color},${color}cc)`,
                        transition:"width 0.8s ease" }} />
        </div>
        <span style={{ color:scoreColor, fontSize:"1.0rem", fontWeight:700, flexShrink:0 }}>
          {r.score}/100
        </span>
        <span style={{ color:RT.textDim, fontSize:"0.95rem", flexShrink:0 }}>
          {r.verdict}
        </span>
      </div>

      {/* Panchang */}
      {r.panchang && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:10 }}>
          {[r.panchang.vara, r.panchang.tithi, r.panchang.nakshatra + " nakshatra"].filter(Boolean).map((tag, i) => (
            <span key={i} style={{ padding:"2px 8px", borderRadius:10, fontSize:"0.95rem",
                                   background:"#fafafa",
                                   border:`1px solid ${RT.border}`,
                                   color:RT.text }}>
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Best time windows */}
      {r.best_times && r.best_times.length > 0 && (
        <div style={{ marginBottom:8 }}>
          <div style={{ color:RT.textDim, fontSize:"0.95rem", fontWeight:600,
                        marginBottom:5 }}>
            Best time to start
          </div>
          {r.best_times.slice(0, 2).map((bt, i) => (
            <div key={i} style={{
              display:"flex", alignItems:"center", gap:8, padding:"5px 8px",
              borderRadius:8, background:"rgba(34,197,94,0.06)",
              border:"1px solid rgba(34,197,94,0.25)", marginBottom:4,
            }}>
              <span style={{ color:"#15803d", fontSize:"0.95rem", fontWeight:700 }}>✓</span>
              <span style={{ color:RT.text, fontSize:"1.0rem" }}>
                {bt.start}–{bt.end} IST
              </span>
              <span style={{ color:"#15803d", fontSize:"0.95rem", marginLeft:"auto" }}>
                {bt.name}
              </span>
            </div>
          ))}
          {r.abhijit && !r.abhijit.weakened && (
            <div style={{
              display:"flex", alignItems:"center", gap:8, padding:"5px 8px",
              borderRadius:8, background:"rgba(245,158,11,0.07)",
              border:"1px solid rgba(245,158,11,0.25)", marginBottom:4,
            }}>
              <span style={{ color:"#a16207", fontSize:"0.95rem", fontWeight:700 }}>☀</span>
              <span style={{ color:RT.text, fontSize:"1.0rem" }}>
                {r.abhijit.start}–{r.abhijit.end} IST
              </span>
              <span style={{ color:"#a16207", fontSize:"0.95rem", marginLeft:"auto" }}>
                Abhijit
              </span>
            </div>
          )}
        </div>
      )}

      {/* Rahu Kalam */}
      {r.rahu_kalam && r.rahu_kalam.start && (
        <div style={{
          display:"flex", alignItems:"center", gap:8, padding:"4px 8px",
          borderRadius:8, background:"rgba(239,68,68,0.06)",
          border:"1px solid rgba(239,68,68,0.25)",
        }}>
          <span style={{ fontSize:"0.95rem" }}>🚫</span>
          <span style={{ color:"#b91c1c", fontSize:"0.98rem", fontWeight:600 }}>
            Rahu Kalam: {r.rahu_kalam.start}–{r.rahu_kalam.end} IST — avoid this window
          </span>
        </div>
      )}
    </div>
  );
}

// ── Section card ───────────────────────────────────────────────────────────────
function SectionCard({ section, index, expanded, onToggle, color }) {
  const icon = getSectionIcon(section.title);
  return (
    <div onClick={onToggle} style={{
      borderRadius:12, overflow:"hidden", cursor:"pointer",
      border: expanded ? `1px solid ${color}60` : `1px solid ${RT.border}`,
      background: expanded ? `rgba(${hexToRgb(color)},0.04)` : RT.card,
      transition:"all 0.2s",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"13px 14px" }}>
        <span style={{ fontSize:"1.2rem", flexShrink:0 }}>{icon}</span>
        <div style={{ flex:1, color: expanded ? color : RT.text,
                      fontWeight:700, fontSize:"1.02rem",
                      fontFamily:RT.font }}>
          {section.title}
        </div>
        <span style={{ color:RT.textDim, fontSize:"0.95rem", flexShrink:0,
                       transform: expanded ? "rotate(180deg)" : "none",
                       transition:"transform 0.2s" }}>▼</span>
      </div>
      {expanded && (
        <div style={{ padding:"0 14px 14px",
                      borderTop:`1px solid ${RT.border}` }}>
          <p style={{ color:RT.text, lineHeight:1.85, fontSize:"1.1rem",
                      margin:"12px 0 0", whiteSpace:"pre-line" }}>
            {section.body}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Activity selector ──────────────────────────────────────────────────────────
function ActivitySelector({ selected, onSelect }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontFamily:RT.font, fontSize:"1.1rem", fontWeight:700,
                      color:RT.text, marginBottom:6 }}>
          What event are you planning?
        </div>
        <div style={{ color:RT.textDim, fontSize:"0.98rem", lineHeight:1.5 }}>
          Your report will scan the right window — marriage gets 6 months,
          travel gets 1 month — matched to how Indians actually plan.
        </div>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {Object.entries(ACTIVITIES).map(([id, act]) => (
          <button
            key={id}
            onClick={() => onSelect(id)}
            style={{
              display:"flex", alignItems:"center", gap:12, padding:"12px 14px",
              borderRadius:12, cursor:"pointer", textAlign:"left",
              background: selected === id
                ? `rgba(${hexToRgb(act.color)},0.08)`
                : RT.card,
              border: selected === id
                ? `1px solid ${act.color}60`
                : `1px solid ${RT.border}`,
              transition:"all 0.2s",
            }}>
            <span style={{ fontSize:"1.4rem" }}>{act.icon}</span>
            <div style={{ flex:1 }}>
              <div style={{ color:RT.text, fontWeight:700, fontSize:"1.05rem",
                fontFamily:RT.font }}>
                {act.label}
              </div>
              <div style={{ color:RT.textDim, fontSize:"0.98rem",
                fontStyle:"italic", fontFamily:RT.font }}>
                Scans next {act.months >= 1 ? act.months + (act.months === 1 ? " month" : " months") : "6 weeks"}
              </div>
            </div>
            {selected === id && (
              <span style={{ color:act.color, fontSize:"1.1rem" }}>✓</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Name prompt ────────────────────────────────────────────────────────────────
function NamePrompt({ activity, onSubmit, onBack }) {
  const [name, setName] = useState("");
  const act = ACTIVITIES[activity];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16, alignItems:"center" }}>
      <button onClick={onBack}
        style={{ alignSelf:"flex-start", background:"transparent", border:"none",
                 color:RT.textDim, fontSize:"0.98rem", cursor:"pointer", padding:0 }}>
        ← Change event
      </button>
      <div style={{ fontSize:"2.5rem" }}>{act?.icon}</div>
      <div style={{ textAlign:"center" }}>
        <div style={{ color:act?.color, fontWeight:700, fontSize:"1.02rem", marginBottom:4 }}>
          {act?.label}
        </div>
        <p style={{ color:RT.text, fontSize:"1.02rem", margin:0, lineHeight:1.6 }}>
          Your report opens with your name.
        </p>
        <p style={{ color:RT.textDim, fontSize:"0.95rem", margin:"4px 0 0" }}>
          The jyotishi will address you directly throughout.
        </p>
      </div>
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === "Enter" && name.trim() && onSubmit(name.trim())}
        placeholder="Your name"
        style={{ width:"100%", maxWidth:280, padding:"12px 16px",
                 background:"#ffffff", border:`1px solid ${RT.border}`,
                 borderRadius:10, color:RT.text, fontSize:"1rem",
                 outline:"none", textAlign:"center" }}
      />
      <button
        disabled={!name.trim()}
        onClick={() => onSubmit(name.trim())}
        style={{
          padding:"12px 32px", borderRadius:24,
          background: name.trim() ? act?.color : "#f0f0f0",
          border:"none",
          color: name.trim() ? "#fff" : RT.textDim,
          fontSize:"1.0rem", fontWeight:700,
          cursor: name.trim() ? "pointer" : "default",
          transition:"all 0.2s",
        }}>
        Generate Muhurta Report — ₹149
      </button>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function MuhurtaReportTab({ userId, noBirth, onNeedBirth }) {
  const [phase,       setPhase]      = useState("select");
  const [activity,    setActivity]   = useState("marriage");
  const [report,      setReport]     = useState(null);
  const [expanded,    setExpanded]   = useState({});
  const [payOrder,    setPayOrder]   = useState(null);
  const [pendingName, setPendingName] = useState("");
  const [payLoading,  setPayLoading] = useState(false);
  const [payError,    setPayError]   = useState("");
  const topRef = useRef(null);

  const act   = ACTIVITIES[activity] || ACTIVITIES["business"];
  const color = act.color;

  const loadRazorpay = () => new Promise(resolve => {
    if (document.getElementById("rzp-script")) { resolve(true); return; }
    const s = document.createElement("script");
    s.id = "rzp-script";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  // Auto-load if already purchased and cached
  useEffect(() => {
    if (!userId || noBirth || phase !== "select") return;
    fetch(`${API}/jyotish/report/muhurta/${encodeURIComponent(userId)}?activity=${activity}`)
      .then(async r => {
        if (r.status === 402) return null;
        return r.json();
      })
      .then(d => {
        if (d?.sections?.length > 0 && !d.error) {
          setReport(d);
          setExpanded({ 0:true, 1:true });
          setPhase("done");
        }
      })
      .catch(() => {});
  }, [userId, activity]);

  const handleGenerate = async (name) => {
    setPhase("loading");
    setPendingName(name);
    topRef.current?.scrollIntoView({ behavior:"smooth" });
    try {
      const res = await fetch(
        `${API}/jyotish/report/muhurta/${encodeURIComponent(userId)}?activity=${activity}&name=${encodeURIComponent(name)}`
      );
      if (res.status === 402) {
        const orderRes = await fetch(`${API}/jyotish/report/create-order`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, report_type: "muhurta" }),
        });
        const order = await orderRes.json();
        if (!order.order_id) { setPhase("error"); return; }
        setPayOrder(order);
        setPhase("payment");
        return;
      }
      const d = await res.json();
      if (d.error) { setPhase("error"); return; }
      setReport(d);
      setExpanded({ 0:true, 1:true });
      setPhase("done");
    } catch {
      setPhase("error");
    }
  };

  const handlePay = async () => {
    setPayLoading(true);
    setPayError("");
    const loaded = await loadRazorpay();
    if (!loaded) { setPayError("Could not load payment SDK."); setPayLoading(false); return; }
    const rzp = new window.Razorpay({
      key:         payOrder.key_id,
      amount:      payOrder.amount,
      currency:    payOrder.currency,
      order_id:    payOrder.order_id,
      name:        "VedSanga",
      description: "Event Muhurta — Auspicious Timing Report",
      image:       "/logo192.png",
      theme:       { color: RT.accent },
      method:      { upi: true, card: true, netbanking: true, wallet: true },
      handler: async (response) => {
        try {
          const vRes = await fetch(`${API}/jyotish/report/verify-purchase`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id:             userId,
              report_type:         "muhurta",
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            }),
          });
          const vData = await vRes.json();
          if (vData.ok) {
            handleGenerate(pendingName);
          } else {
            setPayError("Payment verified but access grant failed. Contact support.");
            setPhase("payment");
          }
        } catch {
          setPayError("Verification failed. Contact support.");
          setPhase("payment");
        }
      },
      modal: { ondismiss: () => { setPhase("select"); setPayLoading(false); } },
    });
    rzp.open();
    setPayLoading(false);
  };

  if (noBirth) {
    return (
      <div style={{ textAlign:"center", padding:"40px 16px" }}>
        <div style={{ fontSize:"2rem", marginBottom:12 }}>🕐</div>
        <p style={{ color:RT.textDim, fontSize:"1rem", lineHeight:1.7 }}>
          Add your birth date and city to generate your Muhurta Report.
        </p>
        <button onClick={onNeedBirth}
          style={{ marginTop:12, padding:"10px 28px", borderRadius:24,
                   background:RT.accent,
                   border:"none", color:"#fff", fontSize:"1rem", fontWeight:600, cursor:"pointer" }}>
          Add Birth Details
        </button>
      </div>
    );
  }

  return (
    <div ref={topRef} style={{ display:"flex", flexDirection:"column", gap:0 }}>

      {/* Activity selection */}
      {phase === "select" && (
        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
          <ActivitySelector selected={activity} onSelect={setActivity} />
          <button
            onClick={() => setPhase("name")}
            style={{
              width:"100%", padding:"13px", borderRadius:24,
              background:color,
              border:"none", color:"#fff",
              fontSize:"1.02rem", fontWeight:700, cursor:"pointer",
            }}>
            Get My Muhurta Dates — ₹149 →
          </button>
        </div>
      )}

      {/* Name entry */}
      {phase === "name" && (
        <NamePrompt
          activity={activity}
          onSubmit={handleGenerate}
          onBack={() => setPhase("select")}
        />
      )}

      {/* Payment */}
      {phase === "payment" && payOrder && (
        <div style={{ display:"flex", flexDirection:"column", gap:16, padding:"8px 0" }}>
          <div style={{ borderRadius:18, padding:"28px 20px",
                        background:RT.cardWarm,
                        border:`1px solid ${RT.border}`, textAlign:"center" }}>
            <div style={{ fontSize:"2.4rem", marginBottom:12 }}>🕐</div>
            <div style={{ fontFamily:RT.font,
                          color:RT.text, fontSize:"1.1rem", fontWeight:700, marginBottom:6 }}>
              Event Muhurta Report
            </div>
            <div style={{ color:RT.textDim, fontSize:"0.98rem", marginBottom:20 }}>
              One-time purchase · All activity types unlocked · Never expires
            </div>
            <div style={{ fontSize:"2.4rem", fontWeight:700,
                          fontFamily:RT.font,
                          color:RT.accent, marginBottom:24 }}>
              ₹{payOrder.amount / 100}
            </div>
            {payError && (
              <div style={{ color:"#b91c1c", fontSize:"0.95rem", marginBottom:14,
                            padding:"8px 12px", background:"rgba(239,68,68,0.06)", borderRadius:8 }}>
                {payError}
              </div>
            )}
            <button onClick={handlePay} disabled={payLoading}
              style={{ width:"100%", padding:"16px", borderRadius:24,
                       background: payLoading ? "#f0f0f0" : RT.accent,
                       border:"none", color: payLoading ? RT.textDim : "#fff",
                       fontSize:"1.05rem", fontWeight:700,
                       cursor: payLoading ? "default" : "pointer",
                       fontFamily:RT.font,
                       boxShadow: payLoading ? "none" : "0 4px 20px rgba(194,86,15,0.35)" }}>
              {payLoading ? "Opening payment..." : "Pay ₹149 — UPI / Card / NetBanking"}
            </button>
            <p style={{ color:RT.textDim, fontSize:"0.9rem", marginTop:12, marginBottom:0 }}>
              🔒 Secured by Razorpay · Report unlocked permanently after payment
            </p>
          </div>
          <button onClick={() => setPhase("select")}
            style={{ background:"transparent", border:"none", color:RT.textDim,
                     fontSize:"0.98rem", cursor:"pointer", padding:"8px 0", textAlign:"center" }}>
            ← Go back
          </button>
        </div>
      )}

      {/* Loading */}
      {phase === "loading" && <Shimmer months={act.months} />}

      {/* Error */}
      {phase === "error" && (
        <div style={{ textAlign:"center", padding:"40px 16px" }}>
          <div style={{ fontSize:"2rem", marginBottom:12 }}>⚠️</div>
          <p style={{ color:RT.textDim, fontSize:"1rem" }}>
            Something went wrong. Please try again.
          </p>
          <button onClick={() => setPhase("select")}
            style={{ marginTop:12, padding:"10px 28px", borderRadius:24,
                     background:"#ffffff", border:`1px solid ${RT.border}`,
                     color:RT.text, fontSize:"1rem", cursor:"pointer" }}>
            Try Again
          </button>
        </div>
      )}

      {/* Done */}
      {phase === "done" && report && (
        <div style={{ display:"flex", flexDirection:"column", gap:0 }}>

          {/* Header */}
          <div style={{ borderRadius:14, padding:"16px",
                        background:`rgba(${hexToRgb(color)},0.05)`,
                        border:`1px solid ${color}40`, marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <span style={{ fontSize:"1.8rem" }}>{act.icon}</span>
              <div>
                <div style={{ color, fontSize:"0.95rem", fontWeight:700 }}>
                  {report.report_name}
                </div>
                <div style={{ color:RT.text, fontWeight:700, fontSize:"1.05rem",
                              fontFamily:RT.font }}>
                  {report.name}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
              {[
                report.moon_rashi  && { label:"Moon",    value:report.moon_rashi },
                report.lagna_rashi && { label:"Lagna",   value:report.lagna_rashi },
                report.character   && { label:"Type",    value:report.character },
                report.scan_months && { label:"Scanned", value:`${report.scan_months} months` },
              ].filter(Boolean).map((item, i) => (
                <div key={i} style={{ padding:"4px 10px", borderRadius:16,
                                      background:"#ffffff",
                                      border:`1px solid ${RT.border}` }}>
                  <span style={{ color:RT.textDim, fontSize:"0.95rem" }}>{item.label}: </span>
                  <span style={{ color:RT.text, fontSize:"0.98rem", fontWeight:600 }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Date cards */}
          {report.top_dates && report.top_dates.length > 0 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ color:RT.textDim, fontSize:"0.95rem", fontWeight:700,
                            marginBottom:10 }}>
                Your Auspicious Dates
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {report.top_dates.map((r, i) => (
                  <DateCard key={r.date} r={r} color={color} idx={i + 1} />
                ))}
              </div>
            </div>
          )}

          {/* Section divider */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
            <div style={{ flex:1, height:1, background:RT.border }} />
            <span style={{ color:RT.textDim, fontSize:"0.95rem", fontWeight:700 }}>
              Jyotishi Guidance
            </span>
            <div style={{ flex:1, height:1, background:RT.border }} />
          </div>

          {/* Sections */}
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {(report.sections || []).map((s, i) => (
              <SectionCard
                key={i} section={s} index={i}
                expanded={!!expanded[i]}
                onToggle={() => setExpanded(prev => ({ ...prev, [i]: !prev[i] }))}
                color={color}
              />
            ))}
          </div>

          {/* Re-generate / change event */}
          <div style={{ display:"flex", gap:8, marginTop:20 }}>
            <button
              onClick={() => { setPhase("select"); setReport(null); }}
              style={{ flex:1, padding:"9px", borderRadius:22,
                       background:"#ffffff", border:`1px solid ${RT.border}`,
                       color:RT.textDim, fontSize:"0.98rem", cursor:"pointer" }}>
              Change event type
            </button>
            <button
              onClick={() => {
                fetch(`${API}/jyotish/report/muhurta/${encodeURIComponent(userId)}?activity=${activity}&force=true&name=${encodeURIComponent(report.name)}`)
                  .then(r => r.json())
                  .then(d => { if (!d.error) { setReport(d); setExpanded({ 0:true, 1:true }); } })
                  .catch(() => {});
              }}
              style={{ flex:1, padding:"9px", borderRadius:22,
                       background:"#ffffff", border:`1px solid ${RT.border}`,
                       color:RT.textDim, fontSize:"0.98rem", cursor:"pointer" }}>
              🔄 Refresh dates
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
      `}</style>
    </div>
  );
}
